import {
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { differenceInMinutes, format } from "date-fns";
import { RefObject, useCallback, useRef, useState } from "react";
import { PinpaperCrossIcon, UserWarningIcon } from "@/components/common/icons";
import { CheckmarkCircle02SolidStandardIcon } from "@/components/common/icons";
import TooltipCrownIcon from "@/components/common/icons/tooltipCrownIcon";
import TooltipWalletIcon from "@/components/common/icons/tooltipWalletIcon";
import { EventTileChips } from "@/components/serviceFlow/events/appointmentEventTileChips";
import { getIconColors } from "@/components/serviceFlow/events/appointmentUtils";
import { TIME_FORMATS } from "@/config";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useResizeObserver from "@/hooks/misc/useResizeObserver";
import { AppointmentStatus } from "@/types";
import {
  MoxieEventContentArg,
  MoxieEventImpl,
  AppointmentExtendedEventProps,
} from "@/types/calendarEventType";
import { TelehealthIcon } from "@/utils/telehealth";
import AppointmentDropdown from "../appointmentDetails/appointmentDropdown";
import { AppointmentStatusBaseCustomTile } from "./appointmentStatusBaseCustomTile";

const useIsNarrowStack = (rootStackRef: RefObject<HTMLDivElement>) => {
  const [isNarrowStack, setIsNarrowStack] = useState(false);

  const onResize = useCallback((rect: DOMRectReadOnly) => {
    setIsNarrowStack(rect.width < 200);
  }, []);

  useResizeObserver(rootStackRef, onResize);

  return isNarrowStack;
};

export const AppointmentCustomTile = ({
  event,
  ...props
}: MoxieEventContentArg<AppointmentExtendedEventProps>) => {
  const {
    id,
    title,
    start,
    end,
    extendedProps: {
      view,
      status,
      reviewStatus,
      invoiceStatus,
      services,
      showDropdown,
      isNewClient,
      hasFormsToFill,
      hasWalletItems,
      clientMembershipsList,
      gfeGeneralStatus,
      eventColorPalette,
      iconColors,
      appointmentIconColors,
      clickable = true,
      isAppointmentTelehealth,
      telehealthAppointmentDetails,
    },
  } = event;

  const theme = useTheme();

  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values["ipad-hor"]}px)`
  );

  const rootStackRef = useRef<HTMLDivElement>(null);
  const isNarrowStack = useIsNarrowStack(rootStackRef);

  const appointmentTime =
    start && end
      ? `${format(start, TIME_FORMATS.COMPACT)} - ${format(end, TIME_FORMATS.COMPACT)}`
      : "";

  const duration = start && end ? differenceInMinutes(end, start) : 0;

  const { showIcons, showChip, ...appointmentRules } = getAppointmentTileRules(
    duration,
    isMobile
  );
  const showWalletAndNewClientIcons =
    showIcons && (duration > 30 || (isMobile && duration >= 45));

  const { iconColor, appointmentIconColor } = getIconColors(
    status,
    eventColorPalette,
    iconColors,
    appointmentIconColors
  );

  const renderIcons = useCallback(
    () => (
      <Stack
        direction={isNarrowStack && duration >= 60 ? "column" : "row"}
        sx={{
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        {showIcons && (
          <Stack
            direction="row"
            sx={{
              justifyContent:
                !isMobile && !isNarrowStack && duration >= 45
                  ? "space-between"
                  : "flex-start",

              flex: 1,
            }}
          >
            {showWalletAndNewClientIcons && (
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {hasWalletItems && <TooltipWalletIcon color={iconColor} />}
                {!!clientMembershipsList && (
                  <TooltipCrownIcon
                    tooltipText={clientMembershipsList}
                    color={iconColor}
                  />
                )}
              </Stack>
            )}
            <AppointmentIcons
              color={appointmentIconColor}
              newClient={isNewClient}
              formsToFillIn={hasFormsToFill}
            />
          </Stack>
        )}
        {showChip && (
          <EventTileChips
            view={view}
            status={status}
            reviewStatus={reviewStatus}
            invoiceStatus={invoiceStatus}
            gfeGeneralStatus={gfeGeneralStatus}
            duration={duration}
          />
        )}
      </Stack>
    ),
    [
      isNarrowStack,
      duration,
      showIcons,
      isMobile,
      showWalletAndNewClientIcons,
      hasWalletItems,
      iconColor,
      clientMembershipsList,
      appointmentIconColor,
      isNewClient,
      hasFormsToFill,
      showChip,
      view,
      status,
      reviewStatus,
      invoiceStatus,
      gfeGeneralStatus,
    ]
  );

  const { multiProviderAppointmentsV1Enabled } = useFeatureFlags();

  if (multiProviderAppointmentsV1Enabled) {
    return <AppointmentStatusBaseCustomTile event={event} {...props} />;
  }

  if (duration < 30) {
    return <ShortAppointmentTile event={event} />;
  }

  const titleServicesSameLine =
    !isNarrowStack && duration <= 45 && duration > 35;

  const showTime =
    appointmentRules.showTime &&
    ((!isMobile && !isNarrowStack) ||
      (isNarrowStack ? duration >= 70 : isMobile && duration >= 45));

  return (
    <Stack
      ref={rootStackRef}
      sx={{
        justifyContent:
          isNarrowStack && duration <= 45 ? "center" : "space-between",
        width: "100%",
        height: "100%",
        cursor: clickable ? "pointer" : "default",
        color: "currentcolor",
        textDecoration: "inherit",
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 0.5,
        }}
      >
        <Typography
          variant="labelTiny"
          noWrap
          sx={{
            color: "currentcolor",
            textDecoration: "inherit",
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: "auto",
            display: "flex",
            mt: duration <= 30 && !isNarrowStack ? -0.35 : 0,
            flexDirection: titleServicesSameLine ? "row" : "column",
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
            {status === AppointmentStatus.CHECKED_IN && (
              <Tooltip title="Client has checked-in" arrow placement="top">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckmarkCircle02SolidStandardIcon
                    color="currentcolor"
                    size={18}
                  />
                </Box>
              </Tooltip>
            )}
            <Typography variant="labelTiny" component="span">
              {title}
            </Typography>
          </Stack>
          {titleServicesSameLine && (
            <Typography component="span" sx={{ width: "8px" }} />
          )}
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
            }}
          >
            {isAppointmentTelehealth && (
              <TelehealthIcon
                telehealthDetails={telehealthAppointmentDetails}
                iconSize="20px"
                iconColor={iconColor}
              />
            )}
            <Typography variant="labelTiny" component="span">
              {services}
            </Typography>
          </Stack>
        </Typography>

        {(showDropdown || (showIcons && duration < 40)) && (
          <Stack
            direction="row"
            sx={{
              gap: 1,
              mt: duration <= 30 && isNarrowStack ? 0.25 : 0,
              justifyContent: "flex-end",
              alignItems: "center",
              flexShrink: 1000,
              flexGrow: 0,
              overflow: "hidden",
              flexWrap: "noWrap",
              minWidth: isNarrowStack ? "38px" : "74px",
            }}
          >
            {duration < 40 && (
              <Box
                sx={{
                  mr: !isNarrowStack ? 4 : 0,
                }}
              >
                {renderIcons()}
              </Box>
            )}
            {showDropdown ? (
              <Box
                sx={{
                  mt: -0.5,
                  position: "absolute",
                  top: 0,
                  right: 0,
                }}
              >
                <AppointmentDropdown
                  appointmentId={id}
                  appointmentStatus={status}
                  reviewStatus={reviewStatus}
                />
              </Box>
            ) : null}
          </Stack>
        )}
      </Stack>
      <Stack
        sx={{
          mt: showTime && duration <= 45 ? -1.5 : 0,
        }}
      >
        {showTime && (
          <Typography variant="labelMicro" noWrap>
            {appointmentTime}
          </Typography>
        )}
        {duration >= 40 && renderIcons()}
      </Stack>
    </Stack>
  );
};

const ShortAppointmentTile = ({
  event,
}: {
  event: MoxieEventImpl<AppointmentExtendedEventProps>;
}) => {
  const {
    id,
    title,
    start,
    end,
    extendedProps: {
      view,
      status,
      reviewStatus,
      invoiceStatus,
      services,
      showDropdown,
      hasWalletItems,
      eventColorPalette,
      iconColors,
      appointmentIconColors,
      clientMembershipsList,
      gfeGeneralStatus,
      clickable = true,
    },
  } = event;

  const theme = useTheme();

  const duration = differenceInMinutes(end, start);

  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values["ipad-hor"]}px)`
  );

  const rootStackRef = useRef<HTMLDivElement>(null);
  const isNarrowStack = useIsNarrowStack(rootStackRef);

  const showIcons = hasWalletItems || !!clientMembershipsList;

  const { iconColor } = getIconColors(
    status,
    eventColorPalette,
    iconColors,
    appointmentIconColors
  );

  return (
    <Stack
      ref={rootStackRef}
      direction="row"
      sx={{
        justifyContent: "space-between",
        mt: "-1px",
        gap: 0.5,
        cursor: clickable ? "pointer" : "default",
        minWidth: 0,
        color: "currentcolor",
        position: "relative",
        textDecoration: "inherit",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Typography
        variant="labelTiny"
        noWrap
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 0.15,
          mr: isNarrowStack ? 4 : 0,
          color: "currentcolor",
          textDecoration: "inherit",
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
        }}
      >
        {title || ""}
        {services && ` • ${services}`}
      </Typography>
      {((!isNarrowStack && showIcons) || showDropdown) && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            gap: 0.5,
            alignItems: "center",
            flexGrow: 0,
            flexShrink: 1000,
            overflow: "hidden",
            flexWrap: "noWrap",
            minWidth: isNarrowStack ? "24px" : "74px",
          }}
        >
          {!isNarrowStack && (
            <Stack
              direction="row"
              sx={{
                gap: 1,
                mt: 0.15,
                flex: 1,
                mr: 4,
              }}
            >
              {!isMobile && (hasWalletItems || !!clientMembershipsList) && (
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    height: 16,
                    overflow: "hidden",
                  }}
                >
                  {hasWalletItems && (
                    <Box
                      sx={{
                        mt: -0.25,
                      }}
                    >
                      <TooltipWalletIcon color={iconColor} />
                    </Box>
                  )}
                  {!!clientMembershipsList && (
                    <Box
                      sx={{
                        mt: -0.25,
                      }}
                    >
                      <TooltipCrownIcon
                        tooltipText={clientMembershipsList}
                        color={iconColor}
                      />
                    </Box>
                  )}
                </Stack>
              )}
              <EventTileChips
                view={view}
                status={status}
                reviewStatus={reviewStatus}
                invoiceStatus={invoiceStatus}
                gfeGeneralStatus={gfeGeneralStatus}
                duration={duration}
              />
            </Stack>
          )}
          {showDropdown && (
            <Box
              sx={{
                mt: duration > 15 ? -1 : -1.5,
                flex: 0,
                position: "absolute",
                top: "15%",
                right: 0,
              }}
            >
              <AppointmentDropdown
                appointmentId={id}
                appointmentStatus={status}
                reviewStatus={reviewStatus}
              />
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
};

function AppointmentIcons({
  newClient,
  formsToFillIn,
  color,
}: {
  newClient?: boolean;
  formsToFillIn?: boolean;
  color: string;
}) {
  if (!newClient && !formsToFillIn) return null;

  return (
    <Stack
      direction="row"
      sx={{
        gap: 0.5,
        alignItems: "center",
      }}
    >
      {newClient && <UserWarningIcon color={color} size="16px" />}
      {formsToFillIn && <PinpaperCrossIcon color={color} size="16px" />}
    </Stack>
  );
}

type AppointmentRules = {
  showIcons: boolean;
  showChip: boolean;
  showTime: boolean;
};

const getAppointmentTileRules = (
  duration: number,
  isMobile = false
): AppointmentRules => {
  if (duration >= 45) {
    return {
      showChip: true,
      showIcons: true,
      showTime: true,
    };
  }

  if (duration >= 30 && duration < 45) {
    return {
      showTime: false,
      showChip: !isMobile,
      showIcons: !isMobile,
    };
  }

  if (duration < 30) {
    return {
      showTime: false,
      showChip: !isMobile,
      showIcons: !isMobile,
    };
  }
};
